import { useState } from 'react';

import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Checkbox, FormikField, Heading, Input, Link, RadioGroup, Text } from '@betterment/samba-react';
import { useFormikContext } from 'formik';

import { BenefitReportInputOptionType } from '~/graphql/schema';

import { BenefitReportCreationOption, BenefitReportInputOption } from './data';
import { ReportCreationOptionsDocument } from './graphql/query.options.generated';
import { ReportFieldsDialog } from './ReportFieldsDialog';

import { urlBuilder } from '../../../utils/urlBuilder';

const reportHasExtraInfo = (reportName: string) => {
  return ['Cash fund', 'Forfeiture fund', 'Suspense fund', 'Unallocated fund'].includes(reportName);
};

const ReportAdditionalInfo = () => {
  return (
    <Text variant="text100">
      Learn more about{' '}
      <Link href={urlBuilder.getUnallocatedFundsGuide()} target="_blank">
        unallocated funds
      </Link>{' '}
      (forfeiture, suspense, and cash)
    </Text>
  );
};

const CheckboxInput = ({ input }: { input: BenefitReportInputOption }) => {
  return (
    <FormikField name={input.attribute}>
      <FormikField.Control>
        <Checkbox css={{ marginTop: 'space.200' }}>
          <Checkbox.Control id={input.attribute} name={input.attribute} />
          <Checkbox.Label>{input.label}</Checkbox.Label>
        </Checkbox>
      </FormikField.Control>
    </FormikField>
  );
};

const DateInput = ({ input }: { input: BenefitReportInputOption }) => {
  const { handleChange, errors } = useFormikContext<BenefitReportCreationOption>();

  return (
    <>
      <Text variant="text100" as="label" css={{ pb: 'space.50' }} htmlFor={input.attribute}>
        {input.label}
      </Text>
      <Input
        css={{ marginBottom: 0, gap: 'space.50', color: 'text.weak' }}
        id={input.attribute}
        name={input.attribute}
        mask="date-mm/dd/yyyy"
        onBlur={handleChange}
        appearance={errors[input.attribute] ? 'critical' : 'default'}
      />
      {errors[input.attribute] && (
        <Text variant="caption" color="text.critical" css={{ pb: 'space.50' }} htmlFor={input.attribute}>
          {input.label} {errors[input.attribute]}
        </Text>
      )}
    </>
  );
};

const isInputTypeDate: (
  inputType: BenefitReportInputOptionType
) => inputType is BenefitReportInputOptionType.Date = inputType => {
  return inputType === BenefitReportInputOptionType.Date;
};

const isInputTypeBoolean: (
  inputType: BenefitReportInputOptionType
) => inputType is BenefitReportInputOptionType.Boolean = inputType => {
  return inputType === BenefitReportInputOptionType.Boolean;
};

export const RunReportOptions = ({ employerId }: { employerId: string }) => {
  const { setFieldValue, setValues, values } = useFormikContext<BenefitReportCreationOption>();
  const { data } = useGraphQLQuery(ReportCreationOptionsDocument, {
    variables: { employerId },
    throwOnError: true,
  });

  const [filter, setFilter] = useState('');
  const filteredReportCreationOptions =
    data?.employer.reportCreationOptions.filter(report => report.name.toLowerCase().includes(filter.toLowerCase())) ||
    [];

  return (
    <>
      <Input
        css={{ marginBottom: 'space.300' }}
        placeholder="Search reports"
        value={filter}
        onChange={event => {
          setFilter(event.target.value);
          void setFieldValue('type', null);
        }}
      />
      <RadioGroup
        value={values.type}
        onValueChange={newValue => {
          void setValues({
            type: newValue,
            start_date: undefined,
            end_date: undefined,
            as_of_date: undefined,
            show_ssn: false,
            show_only_most_recent_changes: false,
            show_only_active_employees: false,
          });
        }}
      >
        {filteredReportCreationOptions.map(report => {
          return (
            <Box key={report.type}>
              <RadioGroup.ItemContainer
                css={{
                  py: 'space.200',
                }}
              >
                <RadioGroup.Item id={report.type} value={report.type} css={{ alignSelf: 'flex-start', mt: '2px' }} />
                <RadioGroup.ItemLabel htmlFor={report.type}>
                  <Heading variant="title100">{report.name}</Heading>
                  <Text as="p">
                    {report.description}{' '}
                    <ReportFieldsDialog
                      reportFields={{
                        name: report.name,
                        description: report.description,
                        columns: report.columns,
                      }}
                    />
                  </Text>

                  {values.type === report.type && (
                    <>
                      {report.inputs.length > 0 && (
                        <Box
                          css={{
                            display: 'inline-flex',
                            flexWrap: 'wrap',
                            width: '100%',
                            pt: 'space.200',
                          }}
                        >
                          {report.inputs?.map(input => (
                            <Box
                              css={{
                                gap: 'space.100',
                                mr: 'space.300',
                                width: isInputTypeBoolean(input.type) ? '90%' : '40%',
                                boxSizing: 'border-box',
                              }}
                              key={`${report.type}-${input.attribute}`}
                            >
                              {isInputTypeDate(input.type) && <DateInput input={input} />}
                              {isInputTypeBoolean(input.type) && <CheckboxInput input={input} />}
                            </Box>
                          ))}
                        </Box>
                      )}
                      {reportHasExtraInfo(report.name) && (
                        <Box css={{ pt: 'space.100' }}>
                          <ReportAdditionalInfo />
                        </Box>
                      )}
                    </>
                  )}
                </RadioGroup.ItemLabel>
              </RadioGroup.ItemContainer>
            </Box>
          );
        })}
      </RadioGroup>
    </>
  );
};
